import { ErrorState } from '@leanin/ui';
import { failureKind, isNotFoundApiError, isTransientRequestFailure } from '@/lib/api-error';
import { NotFoundPage } from '@/components/shell/NotFoundPage';
import { useKeepServerBody, useServerBodyHandoff } from '@/lib/server-body-handoff';
import { LoadErrorState } from './LoadErrorState';
import { RetryButton } from './RetryButton';

type Copy = { readonly title: string; readonly body: string };

type Props = {
  readonly error: Error;
  /** Copy for a transient failure (5xx, timeout, network). Its title also
   *  heads the cause-specific state for a rejected request (401/403/4xx). */
  readonly loadError: Copy;
  /** Entity-specific not-found copy for signed-out visitors; see
   *  `NotFoundPage`. Omitted → the generic signed-out copy. */
  readonly signedOutNotFound?: Copy;
};

// A detail page's failed load. 404 and 410 render the branded not-found page.
// A transient failure gets the page's own retry copy, so one blip never tells
// the viewer the page doesn't exist. Any other rejection is classified by
// `failureKind` like every other load failure, so the page never offers a
// retry that can't work.
//
// On a server-composed page the server body already shows the content this
// load failed to fetch, so a request that failed for a reason outside the
// page (no answer, 5xx, 408, 429) keeps it up instead of the retry surface.
// That includes a crawler whose robots rules stop the page's API request: the
// text it was sent stays the page it renders. The page then loads the failed
// read again (`useServerBodyRecovery`) and hands off when it succeeds.
// Anything else hands off: a rejection (404/410, 401, 403, other 4xx) is a
// real answer, and an error with no request behind it (a bug, a body that did
// not parse) must show.
export function LoadFailure({ error, loadError, signedOutNotFound }: Props) {
  const kept = isTransientRequestFailure(error);
  useKeepServerBody(kept);
  useServerBodyHandoff(!kept);
  if (isNotFoundApiError(error)) {
    return <NotFoundPage signedOut={signedOutNotFound} />;
  }
  if (failureKind(error) !== 'transient') {
    return <LoadErrorState title={loadError.title} error={error} density="page" />;
  }
  return (
    <ErrorState
      density="page"
      title={loadError.title}
      body={loadError.body}
      primary={<RetryButton />}
    />
  );
}
